/**
 * Customer PDF static content schemas (admin "Customer PDF" page).
 *
 * These rows feed the customer share page section that mirrors page 2 of the
 * FIT docket PDF: "A Legacy of Trust & Excellence" (celebrity endorsements)
 * and "Awards & Partners" (award certificates + partner logo wall).
 *
 * The page shows only the first 6 celebrities by `sortOrder`, but the admin
 * can store any number - ordering decides who makes the cut.
 */

import { z } from 'zod';

import { UuidSchema } from './common.js';

// ---------------------------------------------------------------------------
// Celebrities - "A Legacy of Trust & Excellence"
// ---------------------------------------------------------------------------

export const CustomerPdfCelebritySchema = z.object({
  id: UuidSchema,
  name: z.string(),
  description: z.string(),
  imageId: UuidSchema.nullable(),
  sortOrder: z.number().int(),
});
export type CustomerPdfCelebrity = z.infer<typeof CustomerPdfCelebritySchema>;

export const CustomerPdfCelebrityListSchema = z.array(CustomerPdfCelebritySchema);

export const CreateCustomerPdfCelebrityRequestSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().min(1).max(500),
  imageId: UuidSchema.nullish(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});
export type CreateCustomerPdfCelebrityRequest = z.infer<
  typeof CreateCustomerPdfCelebrityRequestSchema
>;

export const UpdateCustomerPdfCelebrityRequestSchema =
  CreateCustomerPdfCelebrityRequestSchema.partial();
export type UpdateCustomerPdfCelebrityRequest = z.infer<
  typeof UpdateCustomerPdfCelebrityRequestSchema
>;

// ---------------------------------------------------------------------------
// Awards - "Awards & Partners" (certificate image + title)
// ---------------------------------------------------------------------------

export const CustomerPdfAwardSchema = z.object({
  id: UuidSchema,
  title: z.string(),
  imageId: UuidSchema.nullable(),
  sortOrder: z.number().int(),
});
export type CustomerPdfAward = z.infer<typeof CustomerPdfAwardSchema>;

export const CustomerPdfAwardListSchema = z.array(CustomerPdfAwardSchema);

export const CreateCustomerPdfAwardRequestSchema = z.object({
  title: z.string().min(1).max(200),
  imageId: UuidSchema.nullish(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});
export type CreateCustomerPdfAwardRequest = z.infer<typeof CreateCustomerPdfAwardRequestSchema>;

export const UpdateCustomerPdfAwardRequestSchema = CreateCustomerPdfAwardRequestSchema.partial();
export type UpdateCustomerPdfAwardRequest = z.infer<typeof UpdateCustomerPdfAwardRequestSchema>;

// ---------------------------------------------------------------------------
// Partner logos - "Awards & Partners" (multi-image logo wall)
// ---------------------------------------------------------------------------

export const CustomerPdfPartnerLogoSchema = z.object({
  id: UuidSchema,
  imageId: UuidSchema,
  sortOrder: z.number().int(),
});
export type CustomerPdfPartnerLogo = z.infer<typeof CustomerPdfPartnerLogoSchema>;

export const CustomerPdfPartnerLogoListSchema = z.array(CustomerPdfPartnerLogoSchema);

/** Replace-set write: the ordered media ids become the whole logo wall. */
export const SetCustomerPdfPartnerLogosRequestSchema = z.object({
  imageIds: z.array(UuidSchema).max(100),
});
export type SetCustomerPdfPartnerLogosRequest = z.infer<
  typeof SetCustomerPdfPartnerLogosRequestSchema
>;

// ---------------------------------------------------------------------------
// Stats strip - "100K+ Happy travellers", "4.4 Rated" etc.
// ---------------------------------------------------------------------------

export const CustomerPdfStatSchema = z.object({
  id: UuidSchema,
  value: z.string(),
  label: z.string(),
  iconImageId: UuidSchema.nullable(),
  rating: z.number().nullable(),
  sortOrder: z.number().int(),
});
export type CustomerPdfStat = z.infer<typeof CustomerPdfStatSchema>;

export const CustomerPdfStatListSchema = z.array(CustomerPdfStatSchema);

export const CreateCustomerPdfStatRequestSchema = z.object({
  value: z.string().min(1).max(60),
  label: z.string().max(120).optional(),
  iconImageId: UuidSchema.nullish(),
  rating: z.number().min(0).max(5).nullish(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});
export type CreateCustomerPdfStatRequest = z.infer<typeof CreateCustomerPdfStatRequestSchema>;

export const UpdateCustomerPdfStatRequestSchema = CreateCustomerPdfStatRequestSchema.partial();
export type UpdateCustomerPdfStatRequest = z.infer<typeof UpdateCustomerPdfStatRequestSchema>;

// ---------------------------------------------------------------------------
// Public share-page payload (image ids already resolved to CDN URLs)
// ---------------------------------------------------------------------------

/** How many celebrities the customer page shows (top-N by sortOrder). */
export const CUSTOMER_PDF_CELEBRITY_DISPLAY_LIMIT = 6;

export const CustomerPdfPublicContentSchema = z.object({
  celebrities: z.array(
    z.object({
      id: UuidSchema,
      name: z.string(),
      description: z.string(),
      imageUrl: z.string(),
    }),
  ),
  awards: z.array(
    z.object({
      id: UuidSchema,
      title: z.string(),
      imageUrl: z.string(),
    }),
  ),
  partnerLogos: z.array(
    z.object({
      id: UuidSchema,
      imageUrl: z.string(),
    }),
  ),
  stats: z.array(
    z.object({
      id: UuidSchema,
      value: z.string(),
      label: z.string(),
      iconUrl: z.string(),
      rating: z.number().nullable(),
    }),
  ),
});
export type CustomerPdfPublicContent = z.infer<typeof CustomerPdfPublicContentSchema>;
